import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import ExportedImage from 'next-image-export-optimizer'
import { Fragment, useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/atoms/ui/button'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '@/components/atoms/ui/carousel'

import { getAssetData } from '@/lib/utils/get-asset-data'

interface OurServiceProps {
  data: Article
}

export const ArticleRecommendationCarousel = ({ data }: OurServiceProps) => {
  const [api, setApi] = useState<CarouselApi>()

  useEffect(() => {
    if (!api) {
      return
    }
  }, [api])

  const articles = useMemo(() => {
    return data?.items
      ?.filter((article) => {
        return article?.fields.popularArticle === true
      })
      .sort((a, b) => new Date(b.sys.updatedAt).getTime() - new Date(a.sys.updatedAt).getTime())
  }, [data.items])

  return (
    <Carousel
      id="top"
      opts={{
        align: 'start',
        slidesToScroll: 1
      }}
      setApi={setApi}
      className="relative space-y-7 lg:space-y-10"
    >
      <CarouselContent>
        {articles.map((item, index) => {
          const imageId = item.fields.img?.sys?.id
          const image = imageId ? getAssetData(imageId, data)?.fields?.file?.url : undefined

          return (
            <CarouselItem key={index} className="">
              <figure className="flex flex-col lg:flex-row lg:items-center lg:max-h-[372px] overflow-hidden border border-gray-300 rounded-xl">
                <ExportedImage
                  src={image ? 'https:' + image : '/static/images/dummy-article.png'}
                  alt={'Gambar Artikel ' + item?.fields.title}
                  width={550}
                  height={372}
                  className="lg:w-[45%] w-full h-56 lg:h-[372px] rounded-t-lg lg:rounded-l-xl lg:rounded-r-none object-cover"
                />
                <figcaption className="flex flex-col p-3.5 lg:px-16 lg:py-10 gap-3 lg:gap-4 lg:w-[55%]">
                  <span className="text-sm font-medium text-primary-blue">{item?.fields.category}</span>
                  <h3 className="text-xl font-bold lg:text-2xl line-clamp-3">{item?.fields.title}</h3>
                  <div className="text-sm line-clamp-5 lg:text-base">
                    {documentToReactComponents(item?.fields.shortDescription as never)}
                  </div>

                  <Button
                    variant="secondary"
                    url={`/${encodeURIComponent(item?.fields.uid)}`}
                    className="font-semibold w-[135px]"
                  >
                    Selengkapnya
                  </Button>
                </figcaption>
              </figure>
            </CarouselItem>
          )
        })}
      </CarouselContent>

      {articles.length > 1 && (
        <Fragment>
          <CarouselPrevious
            className="absolute z-10 flex items-center justify-center w-10 h-10 bg-transparent bg-white border border-gray-300 rounded-full text-primary-blue hover:text-white disabled:text-primary-blue/60 hover:bg-primary-blue top-[33%] lg:top-[37%] -left-3 lg:-left-5 group"
            customArrow={<ChevronLeftIcon className="w-5 h-5" />}
          />
          <CarouselNext
            className="absolute z-10 flex items-center justify-center w-10 h-10 bg-transparent bg-white border border-gray-300 rounded-full text-primary-blue hover:text-white disabled:text-primary-blue/60 hover:bg-primary-blue top-[33%] lg:top-[37%] -right-3 lg:-right-5 group"
            customArrow={<ChevronRightIcon className="w-5 h-5" />}
          />
        </Fragment>
      )}
    </Carousel>
  )
}
